import {
    deepClone,
    fromShiftTag,
    niceDate,
    shiftNumber,
    weekToDayShift,
    shiftTag,
    toISODateString,
    compareNumbers,
} from "./Util.jsx";
import { LINE_TAGS_ACCESS, LINE_TYPES } from "./ManufacturingTags.generated.ts";
import { PEOPLE_SCHEDULING_STRATEGY, PeopleSchedulingStrategyEnum } from "./ManufacturingConsts.generated.ts";
import { translate } from "../components/IntlProviderWrapper.jsx";
import local_settings from "./LocalSettings.jsx";

import type {
    IPeopleSchedulingParams,
    IPersonData,
    IShiftPersonLineRec,
    IShiftPersonLineRecItem,
    IPeopleScheduleLine,
    IAssignPeopleToShiftsByLinesReq,
    IPersonShiftComment,
} from "./backend/manufacturing2.generated.types.ts";

export type PeopleSchedulingParams = {
    strategy: PeopleSchedulingStrategyEnum;
    one_competence_per_shift: boolean;
    one_person_per_line: boolean;
};

export type PersonData = {
    id: number;
    uuid: string;
    name: string;
    short_name: string;
    shift_preference: boolean[];
    competences: number[];
    lineGroupExternalId: string;
};

export type ScheduleStatsPersonShift = {
    hours_assigned: number;
    hours_max: number;
};

export type ScheduleStatsPersonShiftGroup = {
    days_assigned: number;
    days_available: number;
    hours_assigned: number;
    hours_max: number;
};

export type ScheduleStatsPerson = {
    days_assigned: number;
    days_available: number;
    shifts_unassigned: number;
    hours_assigned_sum: number;
    hours_max_sum: number;
    shifts: Map<string, ScheduleStatsPersonShift>;
    shift_groups: Map<number, ScheduleStatsPersonShiftGroup>;
};

type ScheduleStatsShift = {
    duration: number;
    workers_assigned: number;
    workers_required: number;
    workers_available: number;
    lines_assigned: number;
    lines_required: number;
    hours_assigned: number;
    hours_required: number;
};

export type ScheduleStatsLineShift = {
    workers_assigned: number;
    hours_required: number;
};

export type ScheduleStatsLine = {
    workers_assigned_sum: number;
    hours_required_sum: number;
    shifts: Map<string, ScheduleStatsLineShift>;
};

export type ScheduleStats = {
    people: Map<string, ScheduleStatsPerson>;
    shifts: Map<string, ScheduleStatsShift>;
    lines: Map<string, ScheduleStatsLine>;
};

export type PeopleSchedulingStrategyOption = {
    value: PeopleSchedulingStrategyEnum;
    label: string;
};

export type PersonShiftStatusIndicator = {
    key: string;
    label: string;
    description: string;
    variant: "info" | "danger" | "warning" | "success";
};

export type CommentScope = (typeof COMMENT_SCOPE)[keyof typeof COMMENT_SCOPE];

/** Identity of a comment slot. null for shift/lineId means "not applicable" for that scope (e.g. date-only scope). */
export type CommentSlotIdentity = {
    date: string;
    shift: number | null;
    lineId: number | null;
};

/** Comment slot with optional existing comment id (for save payload). */
export type CommentSlotWithExistingId = CommentSlotIdentity & { existingId?: number };

/** Concrete date + shift + line from the schedule (anchor for scoped comments). */
export type PersonShiftLineSlot = {
    date: string;
    shift: number;
    lineId: number;
};

/** Selected person-shift-line slot (the one being edited in the comment modal). */
export type SelectedPersonShiftLine = PersonShiftLineSlot & {
    personId: number;
    personName: string;
    lineTitle: string;
};

/** One assignment slot (date, shift, line) for a person. */
export type PersonAssignment = PersonShiftLineSlot & {
    lineTitle: string;
};

/** Lookup shift-board person comments by person, date, and optional exact shift / line (use null for scoped rows). */
export type GetPersonCommentsFn = (
    personId: number,
    date: string,
    shift?: number | null,
    lineId?: number | null
) => IPersonShiftComment[];

/**
 * One row for the "apply comment to other" table: storage identity plus display fields.
 * When shift or line is not scoped, use null and the UI shows muted "All".
 */
export type RelatedAssignmentsTableRow = {
    slot: CommentSlotIdentity;
    /** null when the row is line-agnostic (date or shift scope) — Line column shows "All". */
    lineTitle: string | null;
};

// FIXME: BOSCH hack
const SHIFT_DURATIONS = [8.5, 8.5, 7];

export const COMMENT_SCOPE = {
    /** Day only */
    date: "date",
    /** Day + shift */
    shift: "shift",
    /** Day + shift + line */
    line: "line",
} as const;

/** Scopes in UI order. Built from {@link COMMENT_SCOPE} so literals stay in sync. */
export const COMMENT_SCOPE_ORDER: CommentScope[] = [COMMENT_SCOPE.date, COMMENT_SCOPE.shift, COMMENT_SCOPE.line];

const SHIFT_NAMES = [
    "ShiftAssignmentPoolEdit.morning_shift",
    "ShiftAssignmentPoolEdit.afternoon_shift",
    "ShiftAssignmentPoolEdit.night_shift",
];

/**
 * Comment storage identity for a scope, from a concrete person–shift–line slot.
 * Date scope: shift and lineId null.
 * Shift scope: lineId null.
 * Line scope: lineId set.
 */
export const commentSlotIdentityForScope = (slot: PersonShiftLineSlot, scope: CommentScope): CommentSlotIdentity => {
    if (scope === COMMENT_SCOPE.date) {
        return { date: slot.date, shift: null, lineId: null };
    }

    if (scope === COMMENT_SCOPE.shift) {
        return { date: slot.date, shift: slot.shift, lineId: null };
    }

    return { date: slot.date, shift: slot.shift, lineId: slot.lineId };
};

/** Get the comment slot identity from a comment. */
export const getCommentSlotIdentityFromComment = (comment: IPersonShiftComment): CommentSlotIdentity => {
    return { date: comment.date, shift: comment.shift_number_day ?? null, lineId: comment.line_id ?? null };
};

/** Get the scope from a comment slot identity. */
export const getScopeFromCommentSlot = (slot: CommentSlotIdentity): CommentScope => {
    if (slot.lineId !== null) {
        return COMMENT_SCOPE.line;
    }

    if (slot.shift !== null) {
        return COMMENT_SCOPE.shift;
    }

    return COMMENT_SCOPE.date;
};

/** Stored comment row for the given scope and anchor slot (at most one match), or undefined. */
export const getPersonCommentForScope = (
    personId: number,
    scope: CommentScope,
    slot: PersonShiftLineSlot,
    getPersonComments: GetPersonCommentsFn
): IPersonShiftComment | void => {
    const identity = commentSlotIdentityForScope(slot, scope);
    return getPersonComments(personId, identity.date, identity.shift, identity.lineId)[0];
};

/**
 * Scope to focus in the UI: line if a line comment exists, else shift if a shift comment exists,
 * else date (least specific).
 */
export const getDefaultCommentScope = (
    personId: number,
    slot: PersonShiftLineSlot,
    getPersonComments: GetPersonCommentsFn
): CommentScope => {
    const lineIdentity = commentSlotIdentityForScope(slot, COMMENT_SCOPE.line);
    if (getPersonComments(personId, lineIdentity.date, lineIdentity.shift, lineIdentity.lineId)[0] !== undefined) {
        return COMMENT_SCOPE.line;
    }

    const shiftIdentity = commentSlotIdentityForScope(slot, COMMENT_SCOPE.shift);
    if (getPersonComments(personId, shiftIdentity.date, shiftIdentity.shift, shiftIdentity.lineId)[0] !== undefined) {
        return COMMENT_SCOPE.shift;
    }

    return COMMENT_SCOPE.date;
};

/**
 * All comments for this person–shift–line cell in one lookup: one {@link GetPersonCommentsFn} call
 * with broad filters, then pick line / shift / date rows for this slot. Order: line, shift, date.
 */
export const getOrderedPersonCommentsForSlot = (
    personId: number,
    slot: PersonShiftLineSlot,
    getPersonComments: GetPersonCommentsFn
): IPersonShiftComment[] => {
    const order = new Map(
        [COMMENT_SCOPE.line, COMMENT_SCOPE.shift, COMMENT_SCOPE.date].map((scope, index) => [scope, index])
    );
    const comments = getPersonComments(personId, slot.date, undefined, undefined).filter(comment => {
        const shift = comment.shift_number_day ?? null;
        const lineId = comment.line_id ?? null;
        return (shift === null || shift === slot.shift) && (lineId === null || lineId === slot.lineId);
    });
    return comments.sort((a, b) => {
        const scopeA = getScopeFromCommentSlot(getCommentSlotIdentityFromComment(a));
        const scopeB = getScopeFromCommentSlot(getCommentSlotIdentityFromComment(b));
        return (order.get(scopeA) ?? 0) - (order.get(scopeB) ?? 0);
    });
};

/**
 * Calculates the number of changed people scheduling parameters compared to defaults.
 * @param {IPeopleSchedulingParams} scheduling_params
 * @param {IPeopleSchedulingParams | void} default_scheduling_params
 * @returns {number}
 */
export const getChangedPeopleSchedulingParamsCount = (
    scheduling_params: IPeopleSchedulingParams,
    default_scheduling_params?: IPeopleSchedulingParams
) => {
    if (default_scheduling_params === undefined) {
        return 0;
    }

    const paramPairs = [
        [scheduling_params.allow_one_competence_per_shift, default_scheduling_params.allow_one_competence_per_shift],
        [
            scheduling_params.allow_one_person_per_single_person_line,
            default_scheduling_params.allow_one_person_per_single_person_line,
        ],
        [scheduling_params.allow_overwork, default_scheduling_params.allow_overwork],
        [scheduling_params.prefer_fully_scheduled_people, default_scheduling_params.prefer_fully_scheduled_people],
        [scheduling_params.consider_dwell_time, default_scheduling_params.consider_dwell_time],
        [scheduling_params.consider_partial_retirement, default_scheduling_params.consider_partial_retirement],
    ];
    return paramPairs.reduce((count, [a, b]) => count + Number(a !== b), 0);
};

/** * Returns a PeopleSchedulingStrategyOption based on the provided strategy.
 * @param {string} strategy - The scheduling strategy to convert.
 * @returns {PeopleSchedulingStrategyOption} - The corresponding PeopleSchedulingStrategyOption.
 * If the strategy is not recognized, it defaults to the "default" strategy.
 */
export const getPeopleSchedulingStrategyOption = (strategy: string): PeopleSchedulingStrategyOption => {
    if (strategy === PEOPLE_SCHEDULING_STRATEGY.more_experienced_first) {
        return {
            value: PEOPLE_SCHEDULING_STRATEGY.more_experienced_first,
            label: translate(
                "ShiftAssignmentPoolEdit.schedule_more_experienced_first",
                "Schedule more experienced first"
            ),
        };
    }

    if (strategy === PEOPLE_SCHEDULING_STRATEGY.less_experienced_first) {
        return {
            value: PEOPLE_SCHEDULING_STRATEGY.less_experienced_first,
            label: translate(
                "ShiftAssignmentPoolEdit.schedule_less_experienced_first",
                "Schedule less experienced first"
            ),
        };
    }

    return {
        value: PEOPLE_SCHEDULING_STRATEGY.default,
        label: translate("ShiftAssignmentPoolEdit.schedule_without_history", "Schedule (without history)"),
    };
};

/** * Returns an array of PeopleSchedulingStrategyOptions for all available scheduling strategies.
 * @returns {PeopleSchedulingStrategyOption[]} - An array of PeopleSchedulingStrategyOptions.
 */
export const getPeopleSchedulingStrategyOptions = (): PeopleSchedulingStrategyOption[] => {
    return Object.keys(PEOPLE_SCHEDULING_STRATEGY).map(getPeopleSchedulingStrategyOption);
};

/**
 * Loads the people scheduling parameters from local storage.
 * @param {string} key - The key to identify the stored parameters.
 * @param {IPeopleSchedulingParams} default_params - The default parameters to use if no stored parameters are found.
 * @returns {IPeopleSchedulingParams} - The loaded or default parameters.
 */
export const loadPeopleSchedulingParams = (
    key: string,
    default_params: IPeopleSchedulingParams
): IPeopleSchedulingParams => {
    const params: IPeopleSchedulingParams = { ...default_params };
    try {
        const stored_params = JSON.parse(local_settings.getItem(key) || "{}");
        if (
            typeof stored_params.strategy === "string" &&
            Object.values(PEOPLE_SCHEDULING_STRATEGY).includes(stored_params.strategy)
        ) {
            params.strategy = stored_params.strategy;
        }

        if (typeof stored_params.allow_one_competence_per_shift === "boolean") {
            params.allow_one_competence_per_shift = stored_params.allow_one_competence_per_shift;
        }

        if (typeof stored_params.allow_one_person_per_single_person_line === "boolean") {
            params.allow_one_person_per_single_person_line = stored_params.allow_one_person_per_single_person_line;
        }

        if (typeof stored_params.allow_overwork === "boolean") {
            params.allow_overwork = stored_params.allow_overwork;
        }

        if (typeof stored_params.ignore_pinned_lines === "boolean") {
            params.ignore_pinned_lines = stored_params.ignore_pinned_lines;
        }

        if (typeof stored_params.prefer_fully_scheduled_people === "boolean") {
            params.prefer_fully_scheduled_people = stored_params.prefer_fully_scheduled_people;
        }

        if (typeof stored_params.consider_dwell_time === "boolean") {
            params.consider_dwell_time = stored_params.consider_dwell_time;
        }

        if (typeof stored_params.consider_partial_retirement === "boolean") {
            params.consider_partial_retirement = stored_params.consider_partial_retirement;
        }
    } catch (error) {
        console.error("Failed to load automatic scheduling params:", error);
    }

    return params;
};

/**
 * Stores the people scheduling parameters in local storage.
 * @param {string} key - The key to identify the stored parameters.
 * @param {IPeopleSchedulingParams} params - The parameters to store.
 */
export const storePeopleSchedulingParams = (key: string, params: IPeopleSchedulingParams) => {
    local_settings.setItem(key, JSON.stringify(params));
};

/**
 * Calculates work hours per person based on workload, number of persons, and line type.
 */
const getWorkHoursPerPerson = (workload: number, persons_count: number, line_type: string): number => {
    if (persons_count === 0) {
        return 0;
    }

    if (line_type === LINE_TYPES.special_activities) {
        return workload;
    }

    return workload / persons_count;
};

/**
 * Computes schedule statistics for people, shifts, and lines.
 */
export const getScheduleStats = (
    lines: IShiftPersonLineRec[],
    pool_line_shifts: IShiftPersonLineRecItem[],
    people_uuids: string[],
    getLineFactor: (line_uuid: string) => number
): ScheduleStats => {
    const stats: ScheduleStats = {
        people: new Map(),
        shifts: new Map(),
        lines: new Map(),
    };

    const assignedPoolShiftTagsByPersonUuid: Map<string, Set<string>> = new Map();
    for (const shift of pool_line_shifts) {
        for (const person of shift.persons) {
            const assignedPoolShiftTags = assignedPoolShiftTagsByPersonUuid.get(person.uuid) ?? new Set();
            assignedPoolShiftTags.add(shift.shift_tag);
            assignedPoolShiftTagsByPersonUuid.set(person.uuid, assignedPoolShiftTags);
        }

        const shift_number = shiftNumber(fromShiftTag(shift.shift_tag));
        // FIXME: BOSCH hack
        // const duration = (shift_number.ts_end - shift_number.ts_start) / TIME_RANGES.HOUR;
        const duration = SHIFT_DURATIONS[shift_number.shift_day];
        stats.shifts.set(shift.shift_tag, {
            duration,
            workers_assigned: 0,
            workers_required: 0,
            workers_available: shift.persons.length,
            lines_assigned: 0,
            lines_required: 0,
            hours_assigned: 0,
            hours_required: 0,
        });
    }

    for (const person_uuid of people_uuids) {
        const assignedPoolShiftTags = assignedPoolShiftTagsByPersonUuid.get(person_uuid) ?? new Set();
        const person_stats: ScheduleStatsPerson = {
            days_assigned: 0,
            days_available: 0,
            shifts_unassigned: assignedPoolShiftTags.size,
            hours_assigned_sum: 0,
            hours_max_sum: 0,
            shifts: new Map(),
            shift_groups: new Map(),
        };

        const available_days_set: Set<number> = new Set();
        const shift_group_to_available_days_map: Map<number, Set<number>> = new Map();
        for (const shift of pool_line_shifts) {
            // const max_h_per_shift = PEOPLE_TAGS_ACCESS.max_h_per_shift(person.tags);
            // const shift_stats = stats.shifts.get(shift.shift_tag);
            // const shift_duration = shift_stats !== undefined ? shift_stats.duration : max_h_per_shift;
            const person_shift_stats: ScheduleStatsPersonShift = {
                hours_assigned: 0,
                // FIXME: BOSCH hack
                // hours_max: Math.min(max_h_per_shift, shift_duration)
                hours_max: SHIFT_DURATIONS[weekToDayShift(shift.shift_number)],
            };
            person_stats.shifts.set(shift.shift_tag, person_shift_stats);

            const shift_number = shiftNumber(fromShiftTag(shift.shift_tag));

            let shift_group_stats = person_stats.shift_groups.get(shift_number.shift_day);
            if (shift_group_stats === undefined) {
                shift_group_stats = {
                    days_assigned: 0,
                    days_available: 0,
                    hours_assigned: 0,
                    hours_max: 0,
                };
                person_stats.shift_groups.set(shift_number.shift_day, shift_group_stats);
            }

            let shift_group_available_days_set = shift_group_to_available_days_map.get(shift_number.shift_day);
            if (shift_group_available_days_set === undefined) {
                shift_group_available_days_set = new Set();
                shift_group_to_available_days_map.set(shift_number.shift_day, shift_group_available_days_set);
            }

            if (assignedPoolShiftTags.has(shift.shift_tag)) {
                person_stats.hours_max_sum += person_shift_stats.hours_max;
                shift_group_stats.hours_max += person_shift_stats.hours_max;
                available_days_set.add(shift_number.day_number);
                shift_group_available_days_set.add(shift_number.day_number);
            }
        }

        person_stats.days_available = available_days_set.size;

        for (const [shift_group, shift_group_stats] of person_stats.shift_groups.entries()) {
            shift_group_stats.days_available = shift_group_to_available_days_map.get(shift_group)?.size ?? 0;
        }

        stats.people.set(person_uuid, person_stats);
    }

    const shift_people_map: Map<string, Set<string>> = new Map();
    const person_to_days_assigned_map: Map<string, Set<number>> = new Map();
    const person_to_shift_group_to_days_assigned_map: Map<string, Map<number, Set<number>>> = new Map();
    for (const line of lines) {
        const line_stats: ScheduleStatsLine = {
            workers_assigned_sum: 0,
            hours_required_sum: 0,
            shifts: new Map(),
        };

        for (const shift of line.shifts) {
            let shift_people_set = shift_people_map.get(shift.shift_tag);
            if (shift_people_set === undefined) {
                shift_people_set = new Set();
                shift_people_map.set(shift.shift_tag, shift_people_set);
            }
            const line_shift_work_hours =
                shift.worker_hours || getWorkHours(shift.shift_tag, getLineFactor(line.line_uuid));
            const line_shift_stats: ScheduleStatsLineShift = {
                workers_assigned: shift.persons.length,
                hours_required: shift.enabled ? line_shift_work_hours : 0,
            };

            line_stats.shifts.set(shift.shift_tag, line_shift_stats);

            // Update line stats
            line_stats.workers_assigned_sum += line_shift_stats.workers_assigned;
            line_stats.hours_required_sum += line_shift_stats.hours_required;

            // Update person stats
            const hours_per_person = getWorkHoursPerPerson(
                line_shift_work_hours,
                shift.persons.length,
                LINE_TAGS_ACCESS.type(line.line_tags)
            );

            const shift_number = shiftNumber(fromShiftTag(shift.shift_tag));
            for (const person of shift.persons) {
                const person_stats = stats.people.get(person.uuid);
                if (person_stats === undefined) {
                    continue;
                }

                const assignedPoolShiftTags = assignedPoolShiftTagsByPersonUuid.get(person.uuid) ?? new Set();
                assignedPoolShiftTags.delete(shift.shift_tag);
                person_stats.shifts_unassigned = assignedPoolShiftTags.size;

                const person_stats_shift = person_stats.shifts.get(shift.shift_tag);
                if (person_stats_shift === undefined) {
                    continue;
                }

                const person_stats_shift_group = person_stats.shift_groups.get(shift_number.shift_day);
                if (person_stats_shift_group === undefined) {
                    continue;
                }

                person_stats_shift.hours_assigned += hours_per_person;
                person_stats.hours_assigned_sum += hours_per_person;

                let person_days_assigned_set = person_to_days_assigned_map.get(person.uuid);
                if (person_days_assigned_set === undefined) {
                    person_days_assigned_set = new Set();
                    person_to_days_assigned_map.set(person.uuid, person_days_assigned_set);
                }

                // Count new assigned days overall
                if (!person_days_assigned_set.has(shift_number.day_number)) {
                    person_days_assigned_set.add(shift_number.day_number);
                    person_stats.days_assigned++;
                }

                let person_shift_group_to_days_assigned_map = person_to_shift_group_to_days_assigned_map.get(
                    person.uuid
                );
                if (person_shift_group_to_days_assigned_map === undefined) {
                    person_shift_group_to_days_assigned_map = new Map();
                    person_to_shift_group_to_days_assigned_map.set(
                        person.uuid,
                        person_shift_group_to_days_assigned_map
                    );
                }

                // Count new assigned days in shift group
                let person_shift_group_days_assigned = person_shift_group_to_days_assigned_map.get(
                    shift_number.shift_day
                );
                if (person_shift_group_days_assigned === undefined) {
                    person_shift_group_days_assigned = new Set();
                    person_shift_group_to_days_assigned_map.set(
                        shift_number.shift_day,
                        person_shift_group_days_assigned
                    );
                }

                if (!person_shift_group_days_assigned.has(shift_number.day_number)) {
                    person_shift_group_days_assigned.add(shift_number.day_number);
                    person_stats_shift_group.hours_assigned += hours_per_person;
                    person_stats_shift_group.days_assigned++;
                }

                shift_people_set.add(person.uuid);
            }

            // Update shift stats
            const shift_stats = stats.shifts.get(shift.shift_tag);
            if (shift_stats === undefined) {
                continue;
            }

            shift_stats.workers_required += line_shift_stats.hours_required / shift_stats.duration;
            shift_stats.lines_assigned += shift.persons.length > 0 ? 1 : 0;
            shift_stats.lines_required += shift.enabled ? 1 : 0;
            shift_stats.hours_assigned += shift.persons.length > 0 ? line_shift_work_hours : 0;
            shift_stats.hours_required += line_shift_stats.hours_required;
        }

        stats.lines.set(line.line_uuid, line_stats);
    }

    for (const [shift_tag, shift_people_set] of shift_people_map) {
        const shift_stats = stats.shifts.get(shift_tag);
        if (shift_stats === undefined) {
            continue;
        }

        shift_stats.workers_assigned = shift_people_set.size;
    }

    return stats;
};

/**
 * Retrieves statistics for a specific line from the schedule statistics.
 */
export const getLineStat = (schedule_stats: ScheduleStats, line_uuid: string): ScheduleStatsLine => {
    const line_stats = schedule_stats.lines.get(line_uuid);
    if (line_stats === undefined) {
        return {
            workers_assigned_sum: 0,
            hours_required_sum: 0,
            shifts: new Map(),
        };
    }

    return line_stats;
};

/**
 * Retrieves statistics for a specific line and shift from the schedule statistics.
 */
export const getLineShiftStat = (
    schedule_stats: ScheduleStats,
    line_uuid: string,
    shift_tag: string
): ScheduleStatsLineShift => {
    const line_stats = schedule_stats.lines.get(line_uuid);
    const shift_stat = line_stats && line_stats.shifts.get(shift_tag);
    if (shift_stat === undefined) {
        return {
            workers_assigned: 0,
            hours_required: 0,
        };
    }

    return shift_stat;
};

/**
 * Retrieves statistics for a specific person and shift from the schedule statistics.
 */
export const getPersonShiftStat = (
    schedule_stats: ScheduleStats,
    person_uuid: string,
    shift_tag: string
): ScheduleStatsPersonShift => {
    const person_stats = schedule_stats.people.get(person_uuid);
    const shift_stat = person_stats && person_stats.shifts.get(shift_tag);
    if (shift_stat === undefined) {
        return {
            hours_assigned: 0,
            hours_max: 0,
        };
    }

    return shift_stat;
};

/**
 * Retrieves statistics for a specific shift tag from the schedule statistics.
 */
export const getShiftStat = (schedule_stats: ScheduleStats, shift_tag: string): ScheduleStatsShift => {
    const shift_stat = schedule_stats.shifts.get(shift_tag);
    if (shift_stat === undefined) {
        return {
            duration: 0,
            workers_assigned: 0,
            workers_required: 0,
            workers_available: 0,
            lines_assigned: 0,
            lines_required: 0,
            hours_assigned: 0,
            hours_required: 0,
        };
    }

    return shift_stat;
};

/**
 * Calculates work hours for a shift tag and factor.
 */
const getWorkHours = (shift_tag: string, factor: number): number => {
    const date = fromShiftTag(shift_tag);
    const shift_number = shiftNumber(date);
    // FIXME: BOSCH hack
    // const hours = (shift_number.ts_end - shift_number.ts_start) / TIME_RANGES.HOUR;
    const hours = SHIFT_DURATIONS[shift_number.shift_day];
    return hours * factor;
};

/**
 * Returns the translation ID for a given shift number.
 */
export const getShiftName = (shift_number: number): string => {
    return SHIFT_NAMES[shift_number];
};

/**
 * Parses a shift tag and returns formatted date and translated shift name.
 *
 * @param shiftTag - The shift tag to parse (format: YYYYWWSS where YYYY is year, WW is week, SS is shift)
 * @returns Object containing formatted date and translated shift name
 *
 * @example
 * getShiftInfo("20241500") // returns { date: "2024-04-08", shiftName: "Morning shift" }
 */
export const getShiftInfo = (shiftTag: string): { date: string; shiftName: string } => {
    if (!shiftTag) {
        return { date: "", shiftName: "" };
    }

    const date = fromShiftTag(shiftTag);
    const shift = shiftNumber(date);
    const dayShift = weekToDayShift(shift.shift);
    const shiftNameKey = getShiftName(dayShift);

    return {
        date: niceDate(date),
        shiftName: translate(shiftNameKey),
    };
};

/**
 * Compares two IShiftPersonLineRecItem objects based on their shift numbers.
 */
export const compareShiftPersonLineRecItems = (a: IShiftPersonLineRecItem, b: IShiftPersonLineRecItem): number => {
    const day_shift_diff = weekToDayShift(a.shift_number) - weekToDayShift(b.shift_number);
    if (day_shift_diff !== 0) {
        return day_shift_diff;
    }

    return a.shift_number - b.shift_number;
};

/**
 * Builds a map of short names for people (person.uuid -> shortened person.name).
 */
export const getPeopleShortNamesMap = (people: IPersonData[]): Map<string, string> => {
    const people_names_map: Map<string, string> = new Map();
    const unique_names: Set<string> = new Set();
    for (const person of people) {
        const name = person.name;
        let unique_name = name;
        let i = 1;
        let space_count = 0;
        let word_length = 0;
        while (i < name.length - 1) {
            if (name[i] === " ") {
                i++;
                space_count++;
                word_length = 0;
                continue;
            }

            word_length++;
            if (space_count > 0 && word_length > 1) {
                unique_name = name.substring(0, i + 1);
                if (i < name.length - 1) {
                    unique_name += ".";
                }

                if (!unique_names.has(unique_name)) {
                    unique_names.add(unique_name);
                    break;
                }
            }

            i++;
        }

        people_names_map.set(person.uuid, unique_name);
    }

    return people_names_map;
};

/**
 * Applies a schedule (from backend or template) to the current shiftboard lines.
 */
export const applyScheduleToShiftboardLines = (
    lines: IShiftPersonLineRec[],
    schedule: IPeopleScheduleLine[],
    getPersonDetails: (person_uuid: string) => PersonData | void
): IShiftPersonLineRec[] => {
    // Build a lookup map: line_uuid -> shift_tag -> [person_uuid]
    const line_shift_people_map: Map<string, Map<string, string[]>> = new Map();
    for (const line of schedule) {
        const shifts: Map<string, string[]> = new Map();
        for (const shift of line.shifts) {
            const shift_tag = shiftTag(shift.year, shift.week, shift.shift);
            shifts.set(shift_tag, shift.people);
        }
        line_shift_people_map.set(line.uuid, shifts);
    }

    // Deep clone lines to avoid mutating props
    const updated_lines: IShiftPersonLineRec[] = deepClone(lines);
    for (const line of updated_lines) {
        for (const shift of line.shifts) {
            const shift_tag = shiftTag(shift.year, shift.week, shift.shift_number);
            const shift_people_map = line_shift_people_map.get(line.line_uuid);
            const people_uuids = shift_people_map && shift_people_map.get(shift_tag);
            if (people_uuids === undefined) {
                continue;
            }

            shift.persons = people_uuids.map(uuid => {
                const person_details = getPersonDetails(uuid);
                return {
                    id: person_details?.id ?? 0,
                    name: person_details?.name ?? "",
                    uuid,
                };
            });
        }
    }

    return updated_lines;
};

/**
 * Prepares the request payload for scheduling people to shifts by lines.
 * @param {IPeopleSchedulingParams} scheduling_params - Scheduling parameters.
 * @param {number} line_group_id - ID of the line group.
 * @param {IShiftPersonLineRec[]} lines - Array of line records.
 * @param {IShiftPersonLineRec} pool_line - Pool line record.
 * @param {string} [selected_shift=""] - Optional selected shift tag.
 * @param {number} [selected_shift_group=-1] - Optional selected shift group.
 * @param {(line: IShiftPersonLineRec) => boolean} [filterLine] - Optional line filter.
 * @returns {IAssignPeopleToShiftsByLinesReq}
 */
export const preparePeopleSchedulingRequest = (
    scheduling_params: IPeopleSchedulingParams,
    line_group_id: number,
    lines: IShiftPersonLineRec[],
    pool_line: IShiftPersonLineRec,
    selected_shift: string = "",
    selected_shift_group: number = -1,
    filterLine: (line: IShiftPersonLineRec) => boolean = () => true
): IAssignPeopleToShiftsByLinesReq => {
    const selectedShiftInfo = selected_shift !== "" ? shiftNumber(fromShiftTag(selected_shift)) : null;

    const shouldIncludeShift = (shift: IShiftPersonLineRecItem): boolean => {
        if (selectedShiftInfo !== null) {
            return shift.shift_tag === selectedShiftInfo.shift_tag;
        }

        if (selected_shift_group !== -1) {
            const shift_day = weekToDayShift(shift.shift_number);
            return shift_day === selected_shift_group;
        }

        return true;
    };

    const shouldIncludeLineShift = (shift: IShiftPersonLineRecItem): boolean => {
        if (selectedShiftInfo !== null) {
            const shiftInfo = shiftNumber(fromShiftTag(shift.shift_tag));
            return (
                shiftInfo.year === selectedShiftInfo.year &&
                shiftInfo.week === selectedShiftInfo.week &&
                shiftInfo.day_number === selectedShiftInfo.day_number
            );
        }

        return true;
    };

    return {
        line_group_id,
        lines: lines.map(line => ({
            uuid: line.line_uuid,
            skip: !filterLine(line),
            shifts: line.shifts.filter(shouldIncludeLineShift).map(shift => ({
                enabled: shift.enabled,
                shift: shift.shift_number,
                week: shift.week,
                year: shift.year,
                people: shift.persons.map(person => person.uuid),
            })),
        })),
        scheduling_params,
        shifts: pool_line.shifts.filter(shouldIncludeShift).map(shift => ({
            shift: shift.shift_number,
            week: shift.week,
            year: shift.year,
        })),
    };
};

/**
 * Gets status indicators for a person assigned to a shift on a given line.
 * @param person_uuid - UUID of the person.
 * @param line - Line record.
 * @param shift - Shift record.
 * @param pool_shift_people_uuids - Set of person UUIDs in the pool shift.
 * @param schedule_stats - Schedule statistics.
 * @param person_details - Person details.
 * @param getPersonCompetenceLevel - Function that returns the competence level of a person on a given line.
 * @param isAbsent - Whether the person is absent during this scheduled shift.
 * @returns Array of status indicators.
 */
export const getPersonShiftStatusIndicators = (
    person_uuid: string,
    line: IShiftPersonLineRec,
    shift: IShiftPersonLineRecItem,
    pool_shift_people_uuids: Set<string>,
    schedule_stats: ScheduleStats,
    person_details: PersonData | void,
    getPersonCompetenceLevel: (person_uuid: string, line_uuid: string) => number,
    isAbsent = false
): PersonShiftStatusIndicator[] => {
    const indicators: PersonShiftStatusIndicator[] = [];

    // Absence indicator
    if (isAbsent) {
        indicators.push({
            key: "absent",
            label: "A",
            description: translate("ShiftAssignmentWeekPool.absent", "Absent"),
            variant: "danger",
        });
    }

    // Competence indicator
    const is_unqualified =
        getPersonCompetenceLevel(person_uuid, line.line_uuid) < 1 &&
        LINE_TAGS_ACCESS.type(line.line_tags) !== LINE_TYPES.special_activities;
    if (is_unqualified) {
        indicators.push({
            key: "unqualified",
            label: "C",
            description: translate("ShiftDetails.IconText.competence"),
            variant: "info",
        });
    }

    // Shift preference indicator
    const day_shift_number = weekToDayShift(shift.shift_number);
    const is_invalid_shift = person_details !== undefined && !person_details.shift_preference[day_shift_number];
    if (is_invalid_shift) {
        indicators.push({
            key: "not-preferred",
            label: "S",
            description: translate("ShiftDetails.IconText.shift"),
            variant: "danger",
        });
    }

    // Pool membership indicator
    const is_in_pool = pool_shift_people_uuids.has(person_uuid);
    if (!is_in_pool) {
        indicators.push({
            key: "not-in-pool",
            label: "P",
            description: translate("ShiftAssignmentPoolEdit.not_in_pool"),
            variant: "danger",
        });
    }

    // Overwork indicator
    const { hours_assigned, hours_max } = getPersonShiftStat(schedule_stats, person_uuid, shift.shift_tag);
    const is_overworked = compareNumbers(hours_assigned, hours_max) > 0;
    if (is_overworked) {
        indicators.push({
            key: "over-assigned",
            label: "!",
            description: translate("ShiftAssignmentPoolEdit.icon_text.too_many_hours"),
            variant: "danger",
        });
    }

    return indicators;
};

/** Stable key for a comment slot identity (checkbox state, deduping). */
export const getCommentSlotIdentityKey = (slot: CommentSlotIdentity): string =>
    `${slot.date}:${String(slot.shift)}:${String(slot.lineId)}`;

/**
 * Rows for the "apply to other" table: unique slots for the selected scope, excluding the anchor
 * slot where applicable (shift scope excludes same date+shift; date scope excludes anchor date).
 * Line scope uses one row per related assignment (anchor line slot already excluded from assignments).
 */
export const getRelatedAssignmentsTableRows = (
    assignments: PersonAssignment[],
    anchor: PersonShiftLineSlot,
    scope: CommentScope
): RelatedAssignmentsTableRow[] => {
    const rowByKey = new Map<string, RelatedAssignmentsTableRow>();
    for (const assignment of assignments) {
        const slot: CommentSlotIdentity = {
            date: assignment.date,
            shift: scope === COMMENT_SCOPE.date ? null : assignment.shift,
            lineId: scope === COMMENT_SCOPE.line ? assignment.lineId : null,
        };

        if (scope === COMMENT_SCOPE.shift && slot.date === anchor.date && slot.shift === anchor.shift) {
            continue;
        }

        if (scope === COMMENT_SCOPE.date && slot.date === anchor.date) {
            continue;
        }

        const key = getCommentSlotIdentityKey(slot);
        if (!rowByKey.has(key)) {
            rowByKey.set(key, {
                slot,
                lineTitle: slot.lineId !== null ? assignment.lineTitle : null,
            });
        }
    }

    return Array.from(rowByKey.values()).sort((x, y) => {
        if (x.slot.date !== y.slot.date) {
            return x.slot.date.localeCompare(y.slot.date);
        }

        if (x.slot.shift !== y.slot.shift) {
            return (x.slot.shift ?? 0) - (y.slot.shift ?? 0);
        }

        return (x.lineTitle ?? "").localeCompare(y.lineTitle ?? "");
    });
};

/**
 * Builds the list of related assignments for a person: all (date, shift, line) slots where the
 * person is assigned in scheduleLines, excluding the selected slot. Used for "apply to other
 * shifts and lines" in the comment modal.
 *
 * @param selected - The currently selected person-shift-line, or null (returns []).
 * @param scheduleLines - All lines with shifts and assigned persons.
 * @param filterLine - Optional filter; only lines that pass are considered.
 * @returns PersonAssignment[] sorted by date, shift, lineId.
 */
export const getRelatedPersonAssignments = (
    selected: SelectedPersonShiftLine | null,
    scheduleLines: IShiftPersonLineRec[],
    filterLine?: (line: IShiftPersonLineRec) => boolean
): PersonAssignment[] => {
    if (!selected) {
        return [];
    }

    const lines = filterLine ? scheduleLines.filter(filterLine) : scheduleLines;
    const assignments: PersonAssignment[] = [];

    for (const line of lines) {
        for (const shift of line.shifts) {
            const isAssigned = shift.persons.some(p => p.id === selected.personId);
            if (!isAssigned) {
                continue;
            }

            const date = toISODateString(fromShiftTag(shift.shift_tag));
            const assignment: PersonAssignment = {
                date,
                shift: weekToDayShift(shift.shift_number),
                lineId: line.line_id,
                lineTitle: line.line_title,
            };

            const isCurrentSlot =
                assignment.date === selected.date &&
                assignment.shift === selected.shift &&
                assignment.lineId === selected.lineId;
            if (isCurrentSlot) {
                continue;
            }

            assignments.push(assignment);
        }
    }

    assignments.sort((a, b) => {
        if (a.date !== b.date) {
            return a.date.localeCompare(b.date);
        }

        if (a.shift !== b.shift) {
            return a.shift - b.shift;
        }

        return a.lineId - b.lineId;
    });

    return assignments;
};

/**
 * Checks if two schedules are equal by comparing the people assigned to the same shifts on the same lines.
 */
export const arePeopleSchedulesEqual = (
    schedule1: IShiftPersonLineRec[],
    schedule2: IShiftPersonLineRec[]
): boolean => {
    // We need to check that the second schedule has the same people assigned to the same shifts on the same lines
    // as the first schedule.
    // For faster lookup, we create a map for the second schedule:
    // line UUID -> shift tag -> person UUIDs set
    const peopleUuidsSetByShiftTagByLineUuid = new Map<string, Map<string, Set<string>>>();
    for (const line of schedule2) {
        const peopleUuidsSetByShiftTag = new Map<string, Set<string>>();
        for (const shift of line.shifts) {
            const people = new Set(shift.persons.map(person => person.uuid));
            peopleUuidsSetByShiftTag.set(shift.shift_tag, people);
        }

        peopleUuidsSetByShiftTagByLineUuid.set(line.line_uuid, peopleUuidsSetByShiftTag);
    }

    for (const line of schedule1) {
        const peopleUuidsSetByShiftTag = peopleUuidsSetByShiftTagByLineUuid.get(line.line_uuid);
        if (!peopleUuidsSetByShiftTag || peopleUuidsSetByShiftTag.size !== line.shifts.length) {
            return false;
        }

        for (const shift of line.shifts) {
            const people = peopleUuidsSetByShiftTag.get(shift.shift_tag);
            if (!people || people.size !== shift.persons.length) {
                return false;
            }

            for (const person of shift.persons) {
                if (!people.has(person.uuid)) {
                    return false;
                }
            }
        }
    }

    return true;
};
